import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap } from 'lucide-react';
import { useCredits } from '../../contexts/CreditsContext';
import { creditLevel, formatChange } from '../../utils/creditLabels';

/**
 * Badge de solde de crédits — toujours visible (intégré à la sidebar globale).
 *
 *  - Affiche « ⚡ {balance} crédits ».
 *  - Couleur selon le seuil : normal (>20) / warning (1–20) / danger (0).
 *  - Micro-animation à chaque `credits:updated` : le nombre « pulse » et la
 *    variation (-1 / +N) s'élève puis s'estompe, pour rendre la consommation
 *    visible au fil de l'eau (y compris les débits WhatsApp en arrière-plan).
 *  - Cliquable : ouvre l'historique des crédits.
 *
 * @param {boolean} expanded - sidebar dépliée (true) ou réduite en icônes (false)
 */
const LEVEL_STYLES = {
  normal: {
    containerCls: 'border-accent-green/20',
    containerStyle: { background: 'rgba(var(--c-accent-green-rgb), 0.07)', color: 'var(--c-text-light)' },
    iconStyle: { color: 'rgb(var(--c-accent-green-rgb))' },
    tooltip: 'Solde de crédits',
  },
  warning: {
    containerCls: '',
    containerStyle: { background: 'rgba(229,162,53,0.07)', borderColor: 'rgba(229,162,53,0.22)', color: 'var(--c-warning)' },
    iconStyle: { color: 'var(--c-warning)' },
    tooltip: 'Solde bas — rechargez bientôt',
  },
  danger: {
    containerCls: '',
    containerStyle: { background: 'rgba(224,84,84,0.07)', borderColor: 'rgba(224,84,84,0.22)', color: 'var(--c-error)' },
    iconStyle: { color: 'var(--c-error)' },
    tooltip: 'Crédits épuisés',
  },
  unknown: {
    containerCls: '',
    containerStyle: { background: 'rgba(var(--c-primary-dark-rgb), 0.6)', borderColor: 'rgba(var(--c-border-rgb), 0.3)', color: 'var(--c-text-gray)' },
    iconStyle: { color: 'var(--c-text-gray)' },
    tooltip: 'Chargement du solde…',
  },
};

export default function CreditBadge({ expanded = true }) {
  const navigate = useNavigate();
  const { balance, lastEvent, loading } = useCredits();

  // Variation transitoire affichée puis effacée (animation).
  const [pop, setPop] = useState(null);

  useEffect(() => {
    if (!lastEvent || !lastEvent.change) return undefined;
    setPop(lastEvent);
    const t = setTimeout(() => setPop(null), 1400);
    return () => clearTimeout(t);
  }, [lastEvent]);

  const level = creditLevel(balance);
  const { containerCls, containerStyle, iconStyle, tooltip } = LEVEL_STYLES[level];
  const isLoadingFirst = loading && balance === null;
  const display = balance === null ? '—' : balance;
  const changePositive = (pop?.change ?? 0) > 0;

  return (
    <button
      type="button"
      onClick={() => navigate('/credits')}
      title={tooltip}
      aria-label={`${tooltip} : ${display} crédits`}
      style={containerStyle}
      className={`relative flex items-center rounded-lg border transition-colors duration-300
        hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-accent-green/50
        ${containerCls}
        ${expanded ? 'gap-2 px-3 py-2 w-full justify-start' : 'gap-1 px-2 py-1.5 justify-center'}`}
    >
      <Zap
        size={expanded ? 18 : 16}
        className={`flex-shrink-0 ${level === 'danger' ? 'animate-pulse' : ''}`}
        style={iconStyle}
        fill="currentColor"
      />

      {/* Le nombre « pulse » à chaque changement de valeur (key = balance). */}
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={display}
          initial={{ scale: 1.35, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 24 }}
          className="font-bold tabular-nums leading-none"
        >
          {isLoadingFirst ? '…' : display}
        </motion.span>
      </AnimatePresence>

      {expanded && (
        <span className="text-xs font-medium opacity-80 leading-none">crédits</span>
      )}

      {/* Variation flottante (-1 / +N) qui s'élève puis disparaît. */}
      <AnimatePresence>
        {pop && (
          <motion.span
            key={pop.id}
            initial={{ opacity: 0, y: 4, scale: 0.8 }}
            animate={{ opacity: 1, y: -16, scale: 1 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.5 }}
            className={`absolute -top-1 ${expanded ? 'right-3' : 'right-1'} text-xs font-bold pointer-events-none
              ${changePositive ? 'text-accent-green' : 'text-red-400'}`}
          >
            {formatChange(pop.change)}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
}
